import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spin, Empty, Collapse, Modal, Tooltip, message } from 'antd';
import { capitalize, startCase } from '../../../util/lodashlike';
import useAlertLevelConfigs from '../hooks/useAlertLevelConfig';
import { statusLabel } from '../../cm/utils/framlSummary';
import {
  DownloadOutlined,
  DeleteOutlined,
  FileTextOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { NetworkLayer } from '../../../services/common';
import { NetworkLayer as TmNetworkLayer } from '../../../services/tmCommon';
import {
  ExportHistoryWrapper,
  ExportHistoryHeader,
  ExportHistoryTitle,
  ExportHistoryTitleIcon,
  ExportHistoryItem,
  ExportHistoryItemHeader,
  ExportHistoryItemTitle,
  ExportHistoryItemStatus,
  ExportHistoryItemMeta,
  ExportHistoryItemActions,
  FiltersAppliedContent,
  FilterItem,
  FilterLabel,
  FilterValue,
  EmptyStateWrapper,
  ExportHistoryBadge,
  EllipsisText
} from './exportHistoryStyled';
const { Panel } = Collapse;

// Modules where isTriggeredOnly is not applicable and should be hidden when false
const MODULES_WITHOUT_TRIGGERED_ONLY = [
  'CRS_TRANSACTION_HISTORY',
  'CM_CRS_TRANSACTION_HISTORY',
  'CM_CASE_CRS_TRANSACTION_HISTORY',
  'CM_MANUAL_CUSTOMER_TRANSACTION_HISTORY',
  // FIN-13176 — the FRAML View queue has no Triggered Only toggle; parseFilters
  // defaults the flag to false, which leaked "Triggered Only: false" onto queue rows.
  'FRAML_CUSTOMER_QUEUE_EXPORT'
];

// FIN-13176 — FRAML queue exports persist the raw BE request (source/riskBand/status
// enums, sortField/sortDir). Map them back to the labels FramlFilterBar shows so the
// audit trail reads as what the analyst picked. Unknown values fall through unchanged.
//
// FIN-13510 — riskBand is deliberately NOT in this static map. It used to hardcode
// { high: 'High', good: 'Good', low: 'Low' }, which shows the DEFAULT band name even when
// a deployment has renamed a band (e.g. "Good" -> "Medium" via alert-level config). Resolved
// at render time instead via useAlertLevelConfigs.getLevelLabel, the same resolver every other
// alert-level-aware surface uses, so a renamed band shows correctly here too. Its fallback
// (capitalize(code)) reproduces the old defaults exactly when no config is loaded.
//
// FIN-12916 — `status` is deliberately NOT in this map either. The Status filter now carries a CM
// WORKFLOW status code (NO_ISSUE, WORK_IN_PROGRESS, ...), so it resolves at render time through
// the SAME `statusLabel` + `workFlowStatusList` the FramlFilterBar dropdown uses — the audit trail
// reads exactly as the option the analyst picked, renamed statuses included. Rows persisted before
// the change carry the old assignment codes (ASSIGNED / PARTIALLY_ASSIGNED / UNASSIGNED), and they
// do NOT all resolve the same way. PARTIALLY_ASSIGNED is not a CM AlertStatus, so it is never in
// the workflow list and falls back to the prettified code ("Partially Assigned"), the old label
// exactly. UNASSIGNED and ASSIGNED ARE CM AlertStatus codes, so when `workFlowStatusList` carries
// them (with a display name) they resolve to the CONFIGURED workflow status name — e.g.
// "Unassigned", or "New" if the deployment renamed it — not the old assignment-filter label; only
// when the list is not loaded (or has no name for them) do they fall back to "Unassigned" /
// "Assigned". Such a legacy row therefore reads as a workflow status. Pinned in
// ExportHistory.test.js (FIN-12916 legacy codes).
// sortField reads "Date", matching the Sort dropdown and the grid column (FIN-12916 row 2).
const FRAML_QUEUE_VALUE_LABELS = {
  source: { aml: 'AML', 'cross-domain': 'Cross-Domain', fraud: 'Fraud' },
  sortField: { framlScore: 'FRAML Score', latestAlertDate: 'Date' },
  sortDir: { asc: 'Ascending', desc: 'Descending' }
};

// FIN-12916 — the filter NAMES must match the FramlFilterBar controls too: the Source filter is
// labelled "Domain" and the riskBand filter "Alert Level" (PRD). startCase would print the wire
// keys ("Source", "Risk Band"). The wire keys themselves are unchanged.
const FRAML_QUEUE_KEY_LABELS = {
  source: 'Domain',
  riskBand: 'Alert Level',
  sortField: 'Sort',
  sortDir: 'Sort Direction'
};

// FIN-13466 — "Filters Applied" is an ALLOWLIST of user-facing filter keys per module family.
// It used to be a denylist rendering `...rest`, so every server-owned or internal field the BE
// persists into filter_params leaked onto the panel: framlViewVersion, framlAlertSources (an
// object, printed as "[object Object]") and framlFocalTransactionIds on every FRAML alert, CM
// FRAML and FRAML customer row. A new BE field now stays hidden until it is
// added here on purpose.
//
// Transaction + Activity history rows (TransactionsHistoryRequest / TriggeredActivityHistoryRequest
// asJson). An alert-scoped listing mixes both, so one union list serves the family. from/to render
// as the combined "Date Range" row. minAmount/maxAmount stay hidden (decided, FIN-13466).
const HISTORY_FILTER_KEYS = [
  'isTriggeredOnly',
  // FIN-14025 — QA/PM asked for these back (FIN-13466 hid them): the download's zone, the
  // transaction-date sort and the tenant time adjustment. The 6.4 BE does not persist
  // timeAdjustmentMinutes yet, so that row only renders once a request carries it.
  'zoneId',
  'sortOrder',
  'timeAdjustmentMinutes',
  'typologyId',
  'variantId',
  'transactionType',
  'accountId',
  'transactionId',
  'counterpartyId',
  'counterpartyName',
  'isCredit',
  'destinationCountryCode',
  'highRiskCountryFlag',
  // FIN-13688 — canonical FRAML customer grid Search-by
  'partyId',
  'senderId',
  'receiverId',
  'senderName',
  'receiverName',
  // FIN-13748 — canonical FRAML Advanced Filters
  'senderAccount',
  'receiverAccount',
  'transactionTypeDescription',
  // FIN-11648 — Activity tab search + facets
  'activityId',
  'partyKey',
  'ipAddress',
  'activityType',
  'activityStatus',
  'channel'
];

// FIN-13748 — the canonical FRAML alert grid names the code filter "Transaction Type Code" and the
// description filter "Transaction Type", so its rows use the same labels. Keyed by the row's own
// module: an alert-scoped listing mixes these rows with TM and activity ones.
const FRAML_ALERT_TRANSACTION_MODULES = [
  'FRAML_ALERT_TRANSACTION_HISTORY',
  'CM_FRAML_ALERT_TRANSACTION_HISTORY',
  'CM_CASE_FRAML_ALERT_TRANSACTION_HISTORY'
];
const FRAML_ALERT_KEY_LABELS = {
  transactionType: 'Transaction Type Code',
  transactionTypeDescription: 'Transaction Type'
};
const isFramlAlertTransactionRow = (item) =>
  FRAML_ALERT_TRANSACTION_MODULES.includes(
    (item.module || '').toUpperCase().replace(/-/g, '_')
  );

// FRAML View queue rows (FramlQueueExportRequest asJson). The queue's sort IS a user choice there
// (the Sort dropdown), so sortField/sortDir stay; offset/limit/download/moduleId/zoneId do not.
const FRAML_QUEUE_FILTER_KEYS = [
  'search',
  'source',
  'riskBand',
  'status',
  'sortField',
  'sortDir'
];

const isScalarFilterValue = (value) =>
  value !== null &&
  value !== undefined &&
  ['string', 'number', 'boolean'].includes(typeof value);

// STATUS_CONFIG covers the four lifecycle states a BE export can be in.
// `queued` is the just-enqueued state (no worker has picked it up yet) and
// `processing` is the BE-canonical synonym of `inprogress`. Both render with
// the clock-circle icon so a Queued row visually reads the same as Inprogress
// — colour and icon stay stable while the BE label flips. Forward-compat
// with FIN-11649 BE async-flow which emits queued/processing distinctly.
const STATUS_CONFIG = {
  completed: { color: '#52c41a', icon: <CheckCircleOutlined /> },
  inprogress: { color: '#1890ff', icon: <ClockCircleOutlined /> },
  processing: { color: '#1890ff', icon: <ClockCircleOutlined /> },
  queued: { color: '#1890ff', icon: <ClockCircleOutlined /> },
  failed: { color: '#ff4d4f', icon: <CloseCircleOutlined /> }
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  return moment(timestamp).format('DD/MM/YYYY HH:mm');
};

const truncateAlertId = (alertId, maxLen = 16) => {
  if (!alertId) return '';
  return alertId.length > maxLen
    ? `${alertId.substring(0, maxLen)}...`
    : alertId;
};

// Lazily constructed: building a NetworkLayer at module scope runs inside the
// services/api -> keycloak -> authentication -> util -> appUtils -> crsRiskDetail
// import cycle, while apiModules/CommonPageModules is still initialising and
// `CommonModule` is not yet exported. Deferring to first use keeps the single
// instance and breaks the cycle.
let _commonService;
const getCommonService = () => {
  if (!_commonService) _commonService = new NetworkLayer();
  return _commonService;
};
const tmService = new TmNetworkLayer();

const ExportHistory = ({
  alertId,
  caseId,
  partyId,
  isActive,
  isCaseLevel = false,
  title = 'Export History for this Alert',
  module,
  isManualCase = false,
  refreshKey,
  alertLevels = [],
  workFlowStatusList = []
}) => {
  // FIN-13510 — resolves the configured display name for a FRAML risk-band code
  // (e.g. a deployment that renamed "Good" -> "Medium"). Falls back to
  // capitalize(code) when no config is loaded, matching the old hardcoded defaults.
  const { getLevelLabel } = useAlertLevelConfigs(alertLevels);
  const framlQueueLabel = useCallback(
    (key, value) =>
      key === 'riskBand'
        ? getLevelLabel(value)
        : key === 'status'
        ? statusLabel(value, workFlowStatusList)
        : FRAML_QUEUE_VALUE_LABELS[key]?.[value] ?? String(value),
    [getLevelLabel, workFlowStatusList]
  );
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState([]);
  const [typologyMap, setTypologyMap] = useState({});

  const fetchExportHistory = useCallback(async () => {
    // FIN-11360: queue-level exports (e.g. FRAML View) are scoped only by
    // `module` (no alert/case/party). Allow the module-only fetch through — the
    // else-branch below already sets params.module and the BE scopes by module+user.
    if (!alertId && !caseId && !partyId && !module) return;
    setIsLoading(true);
    try {
      // FIN-11649 / Defect 6 — Download History lists ALL exports for the
      // alert (Transaction + Activity), which the BE persists under different
      // module keys (CM_FRAML_ALERT_TRANSACTION_HISTORY vs TM_ACTIVITY).
      // Constraining the alert-scoped list to a single `module` filtered out
      // the Activity jobs, so they were permanently invisible in the UI. Drop
      // the module constraint for alert-scoped fetches and let the BE return
      // every job for the alert. The `module` prop is still used downstream
      // (enqueue/title) but must NOT narrow the listing.
      const params = {};
      if (alertId) {
        params.alertId = alertId;
      } else {
        // case/party-scoped listings keep the module key — those contexts are
        // single-module and BE still scopes by it.
        if (module) params.module = module;
        if (caseId) params.caseId = caseId;
        if (partyId) params.partyId = partyId;
      }

      const response = await getCommonService().amlsExportHistoryAPI(params);
      setData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to fetch export history:', error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [alertId, caseId, partyId, module]);

  useEffect(() => {
    if (isActive) {
      fetchExportHistory();
    }
  }, [isActive, refreshKey, fetchExportHistory]);

  useEffect(() => {
    if (!alertId) return;
    tmService
      .tmCommonTransactionTriggeredTypologiesAPI({ alertId })
      .then((res) => {
        if (Array.isArray(res)) {
          const map = {};
          res.forEach(({ id, name }) => {
            map[id] = name;
          });
          setTypologyMap(map);
        }
      })
      .catch(() => {});
  }, [alertId]);

  const handleDownload = useCallback(async (jobId) => {
    setDownloadingIds((prev) => [...prev, jobId]);
    try {
      const response = await getCommonService().amlsDownloadUrlAPI(jobId);
      if (response?.downloadUrl) {
        window.open(response.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to fetch download URL:', error);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== jobId));
    }
  }, []);

  const deleteExportHistoryItem = useCallback(
    async (id) => {
      setIsLoading(true);
      try {
        await getCommonService().amlsDeleteExportHistoryAPI(id);
        message.success('Export deleted');
        await fetchExportHistory();
      } catch (error) {
        // FIN-11542 — surface the rejection to the analyst instead of
        // swallowing it in a console.error. A failed delete (BE 403 ownership
        // scope / 404 / in-progress job) previously looked like "nothing
        // happened"; now it shows the BE-provided reason where available.
        console.error('Failed to delete export history item:', error);
        const reason =
          error?.response?.data?.msg ||
          error?.data?.msg ||
          error?.message ||
          'Failed to delete export. Please try again.';
        message.error(reason);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchExportHistory]
  );

  const handleDelete = useCallback(
    (id) => {
      Modal.confirm({
        title: 'Delete Export',
        content: 'Are you sure you want to delete this export?',
        okText: 'Delete',
        okType: 'danger',
        onOk: () => {
          deleteExportHistoryItem(id);
        }
      });
    },
    [deleteExportHistoryItem]
  );

  // FIN-13466 — allowlist, see HISTORY_FILTER_KEYS / FRAML_QUEUE_FILTER_KEYS. Internal params
  // (ids, limit/offset — FIN-12016, download flags, moduleId/module, framl*)
  // are simply never picked; non-scalar values are dropped so nothing prints "[object Object]".
  const parseFilters = (filterParam) => {
    try {
      const parsed = JSON.parse(atob(filterParam || ''));
      if (!parsed || typeof parsed !== 'object') return {};
      const isFramlQueue = module === 'FRAML_CUSTOMER_QUEUE_EXPORT';
      const allowedKeys = isFramlQueue
        ? FRAML_QUEUE_FILTER_KEYS
        : HISTORY_FILTER_KEYS;
      const { from, to } = parsed;
      const filters = {};
      if (!isFramlQueue && from && to) {
        filters['Date Range'] = `${moment(from).format(
          'DD/MM/YYYY'
        )} to ${moment(to).format('DD/MM/YYYY')}`;
      }
      if (!isFramlQueue) {
        // Triggered Only defaults to false when absent; the render step hides it where the
        // toggle does not exist (MODULES_WITHOUT_TRIGGERED_ONLY / manual case).
        filters.isTriggeredOnly = false;
      }
      allowedKeys.forEach((key) => {
        if (isScalarFilterValue(parsed[key])) filters[key] = parsed[key];
      });
      return filters;
    } catch {
      return {};
    }
  };

  const buildTitle = useCallback(
    (item, filters, { truncate = true } = {}) => {
      const alertId = truncate ? truncateAlertId(item.alertId) : item.alertId;
      const dateRange = filters?.['Date Range'];

      // FIN-11649 / B4 — Download History now contains both Transaction and
      // Activity exports. Distinguish each row via item.module.
      //
      // The downloads table persists module as the canonical UPPER_SNAKE
      // enum form (AsyncDownloadModule.entryName), e.g. TM_ACTIVITY,
      // TM_TRANSACTION_HISTORY, CM_FRAML_ALERT_TRANSACTION_HISTORY — NOT the
      // lowercase-kebab FE wire format ('tm-activity') that the enqueue path
      // sends. Earlier the prefix only matched the kebab form, so it never
      // showed on a stored row. Normalise the comparison: classify by whether
      // the upper-cased module string contains ACTIVITY (every activity module
      // does: TM_ACTIVITY / CM_TM_ACTIVITY / CM_CASE_TM_ACTIVITY / …) or
      // TRANSACTION (every transaction module does). Both wire formats
      // (kebab + upper-snake) round-trip.
      let prefix = '';
      const moduleKey = (item.module || '').toUpperCase().replace(/-/g, '_');
      if (moduleKey.includes('ACTIVITY')) {
        prefix = 'Activity export — ';
      } else if (moduleKey.includes('TRANSACTION')) {
        prefix = 'Transaction export — ';
      } else if (moduleKey.includes('FRAML')) {
        return 'FRAML Queue Export';
      }

      if (isCaseLevel && caseId) {
        return `${prefix}Case ${caseId} - All Transactions`;
      }

      if (dateRange) {
        return `${prefix}Alert ${alertId} from ${dateRange}`;
      }

      return `${prefix}Alert ${alertId}`;
    },
    [isCaseLevel, caseId]
  );

  return (
    <ExportHistoryWrapper>
      <Spin spinning={isLoading}>
        <ExportHistoryHeader>
          <ExportHistoryTitle>
            <ExportHistoryTitleIcon>
              <FileTextOutlined />
            </ExportHistoryTitleIcon>
            {title}
            <ExportHistoryBadge count={data.length} />
          </ExportHistoryTitle>
        </ExportHistoryHeader>

        {data.length === 0 ? (
          <EmptyStateWrapper>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No export history found"
            />
          </EmptyStateWrapper>
        ) : (
          data.map((item) => {
            const status = item.status || '';
            const normalizedStatus = status.toLowerCase();
            const statusConfig =
              STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.inprogress;
            const isCompleted = normalizedStatus === 'completed';
            const parsedFilters = parseFilters(item.filterParam);
            const isFailedStatus = normalizedStatus === 'failed';
            const tooltipTitle = isFailedStatus ? item.errorMessage : null;

            return (
              <ExportHistoryItem key={item.jobId}>
                <ExportHistoryItemHeader>
                  <Tooltip
                    title={buildTitle(item, parsedFilters, {
                      truncate: false
                    })}>
                    <ExportHistoryItemTitle>
                      {buildTitle(item, parsedFilters)}
                    </ExportHistoryItemTitle>
                  </Tooltip>
                  <Tooltip title={tooltipTitle} placement="top">
                    <ExportHistoryItemStatus $color={statusConfig.color}>
                      {statusConfig.icon} {capitalize(status)}
                    </ExportHistoryItemStatus>
                  </Tooltip>
                </ExportHistoryItemHeader>

                <ExportHistoryItemMeta>
                  <span>{formatTimestamp(item.createdAt)}</span>
                  <span>&bull;</span>
                  <span>
                    {item.recordCount} record{item.recordCount === 1 ? '' : 's'}
                  </span>
                </ExportHistoryItemMeta>
                {item.alertId && (
                  <ExportHistoryItemMeta>
                    <Tooltip title={item.alertId}>
                      <EllipsisText>Alert: {item.alertId}</EllipsisText>
                    </Tooltip>
                  </ExportHistoryItemMeta>
                )}
                {item.caseId && (
                  <ExportHistoryItemMeta>
                    <span>Case: {item.caseId}</span>
                  </ExportHistoryItemMeta>
                )}

                {Object.keys(parsedFilters).length > 0 && (
                  <Collapse
                    ghost
                    expandIconPosition="start"
                    style={{ marginTop: 4 }}>
                    <Panel
                      header={
                        <span style={{ fontSize: 13, color: '#556069' }}>
                          <FilterOutlined style={{ marginRight: 4 }} />
                          Filters Applied
                        </span>
                      }
                      key="filters">
                      <FiltersAppliedContent>
                        {Object.entries(parsedFilters).map(([key, value]) => {
                          if (
                            key === 'isTriggeredOnly' &&
                            !value &&
                            (MODULES_WITHOUT_TRIGGERED_ONLY.includes(module) ||
                              isManualCase)
                          )
                            return null;
                          // FIN-12079: "all" is the no-filter sentinel for the
                          // Variants dropdown — don't render it as an applied filter.
                          if (
                            key === 'variantId' &&
                            (value === 'all' || value === '' || value == null)
                          )
                            return null;
                          return (
                            <FilterItem key={key}>
                              <FilterLabel>
                                {isFramlAlertTransactionRow(item) &&
                                FRAML_ALERT_KEY_LABELS[key]
                                  ? FRAML_ALERT_KEY_LABELS[key]
                                  : key === 'typologyId'
                                  ? 'Typology'
                                  : key === 'isTriggeredOnly'
                                  ? 'Triggered Only'
                                  : key === 'accountId'
                                  ? 'Account Number'
                                  : key === 'transactionType'
                                  ? 'Transaction Type'
                                  : key === 'variantId'
                                  ? 'Variant'
                                  : module === 'FRAML_CUSTOMER_QUEUE_EXPORT' &&
                                    FRAML_QUEUE_KEY_LABELS[key]
                                  ? FRAML_QUEUE_KEY_LABELS[key]
                                  : startCase(key)}
                                :
                              </FilterLabel>
                              <FilterValue>
                                {key === 'typologyId'
                                  ? typologyMap[value] || value
                                  : // FIN-14025 — the BE persists a UTC ZoneOffset by its id, "Z".
                                  key === 'zoneId' && value === 'Z'
                                  ? 'UTC'
                                  : module === 'FRAML_CUSTOMER_QUEUE_EXPORT'
                                  ? framlQueueLabel(key, value)
                                  : String(value)}
                              </FilterValue>
                            </FilterItem>
                          );
                        })}
                      </FiltersAppliedContent>
                    </Panel>
                  </Collapse>
                )}

                <ExportHistoryItemActions>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={downloadingIds.includes(item.jobId)}
                    onClick={() => handleDownload(item.jobId)}
                    disabled={!isCompleted}>
                    Download
                  </Button>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(item.jobId)}
                  />
                </ExportHistoryItemActions>
              </ExportHistoryItem>
            );
          })
        )}
      </Spin>
    </ExportHistoryWrapper>
  );
};

ExportHistory.propTypes = {
  alertId: PropTypes.string,
  caseId: PropTypes.string,
  partyId: PropTypes.string,
  isActive: PropTypes.bool,
  isCaseLevel: PropTypes.bool,
  title: PropTypes.string,
  module: PropTypes.string,
  isManualCase: PropTypes.bool,
  refreshKey: PropTypes.number,
  alertLevels: PropTypes.array,
  // FIN-12916 — CM workflow statuses ({code, currentValue}); labels the FRAML queue Status filter.
  workFlowStatusList: PropTypes.array
};

export default ExportHistory;
